import pageTitle from 'ember-page-title/helpers/page-title';
import Component from '@glimmer/component';
import PageSetup from 'bcl/components/page-setup';
import { concat } from '@ember/helper';
import displayDate from 'bcl/helpers/display-date';
import displayWeekDay from 'bcl/helpers/display-week-day';
import sort from 'bcl/helpers/sort';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import WaitingSpinner from 'bcl/components/waiting-spinner';
import { action } from '@ember/object';
import eq from 'ember-truth-helpers/helpers/eq';
import type SessionService from 'bcl/services/session';
import type DownloadService from 'bcl/services/download';
import type Router from 'bcl/router';

interface OrderShowSignature {
  // The arguments accepted by the component
  Args: {
    model: {
      company: CompanyModel;
      order: OrderModel;
    };
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}
export default class OrderShowComponent extends Component<OrderShowSignature> {
  @service declare download: DownloadService;
  @service declare session: SessionService;
  @service declare router: Router;

  @action
  async startDownload() {
    const order = this.args.model.order;
    await this.download.order(order.id);
  }
  @action
  async startDownloadOld() {
    const order = this.args.model.order;
    await this.download.order_old(order.id);
  }
  @action cancel() {
    this.args.model.order.rollbackAttributes();
    this.router.transitionTo('auth.orders');
  }
  @action async confirmDelete() {
    if (
      confirm(
        'Bestellung wirklich löschen? Es könnte zu ungewollten Effekten in Bezug auf Bestellungen kommen.'
      )
    ) {
      this.args.model.order.status = 'canceled';
      this.args.model.order.save();
      this.router.transitionTo('auth.orders');
    }
  }

  <template>
    {{pageTitle (concat "Bestellung " @model.order.id " anzeigen")}}
    <PageSetup @title={{concat "Bestellung " @model.order.id " anzeigen"}}>
      <:header>
        {{#if (eq @model.order.status "canceled")}}
          (bereits gelöscht)
        {{else}}
          <button
            type="button"
            {{on "click" this.confirmDelete}}
            class="ml-1 rounded px-2 py-1 text-xs font-semibold text-white shadow-sm bg-red-600 hover:bg-red-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
          >löschen</button>
        {{/if}}
        <button
          type="button"
          {{on "click" this.startDownload}}
          class="ml-1 rounded px-2 py-1 text-xs font-semibold text-white shadow-sm bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >herunterladen
          {{#if this.download.isDownloading}}
            <WaitingSpinner class="inline ml-2" />
          {{/if}}
        </button>
        {{#if this.session.isSpruce}}
          <button
            type="button"
            {{on "click" this.startDownloadOld}}
            class="ml-1 rounded px-2 py-1 text-xs font-semibold text-white shadow-sm bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >herunterladen old
            {{#if this.download.isDownloading}}
              <WaitingSpinner class="inline ml-2" />
            {{/if}}
          </button>
        {{/if}}
      </:header>
      <:default>
        {{! start:print }}
        <div class="px-4 lg:px-8 print:text-sm">
          <div class="sm:items-center grid grid-cols-2">
            <div class="mt-32">
              <span class="font-bold block">
                {{@model.order.supplier.name}}
              </span>
              <div>
                {{@model.order.supplier.street}}
                {{@model.order.supplier.street_number}}
                <br />
                {{@model.order.supplier.zip_code}}
                {{@model.order.supplier.place}}
                <br />
                {{@model.order.supplier.email}}
                <br />
                {{#if @model.order.supplier.fax}}
                  Fax:
                  {{@model.order.supplier.fax}}
                  <br />
                {{/if}}
                Kundennummer:
                {{@model.order.supplier.customer_number}}
              </div>
              <span class="font-bold block mt-6">
                Bestellung
              </span>
              <span class="block mt-8">
                Bitte liefern Sie zum
                <div class="block">
                  <span class="font-bold">
                    {{displayWeekDay @model.order.delivery_date}},
                    {{displayDate @model.order.delivery_date}}
                    <br />
                    {{@model.order.supplier.delivery_time}}</span>:
                </div>
              </span>
            </div>
            <div class="mt-0 col-start-2 text-right">
              <span class="font-bold block">
                {{@model.company.name}}
              </span>
              {{@model.company.street}}
              {{@model.company.street_number}}<br />
              {{@model.company.zip_code}}
              {{@model.company.place}}
              <br />
              Tel:
              {{@model.company.telefon_number}}
              <br />
              Fax:
              {{@model.company.fax_number}}
              <br />
              Email:
              {{@model.company.email}}
              <br />
              St-Nr:
              {{@model.company.tax_number}}
              <br />
              {{@model.company.contact_person}}
              <br />
              <br />
              {{displayDate @model.order.created_at}}
            </div>

          </div>
          <div>
            <div>
              <div class="mt-4">
                <table class="w-full">
                  <thead class="bg-gray-50 print:bg-white">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 w-1/2"
                      >Artikel</th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >Art-Nr.</th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >Gebinde</th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >Menge</th>
                      {{#if (eq @model.order.status "delivered")}}
                        <th
                          scope="col"
                          class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >Geliefert</th>
                      {{/if}}
                    </tr>
                  </thead>
                  <tbody class="bg-white">
                    {{#each
                      (sort @model.order.orderEntries "position")
                      as |ordEnt|
                    }}
                      <tr>
                        <td
                          class="py-3 print:py-0.5 whitespace-nowrap pr-3 text-sm font-medium text-gray-900"
                        >
                          {{ordEnt.supplier_item.item.name}}

                        </td>
                        <td
                          class="py-3 print:py-0.5 whitespace-nowrap px-3 text-sm text-gray-500"
                        >
                          {{ordEnt.supplier_item.articlenr}}

                        </td>
                        <td
                          class="py-3 print:py-0.5 whitespace-nowrap px-3 text-sm text-gray-500"
                        >
                          {{ordEnt.supplier_item.item.gebinde}}

                        </td>
                        <td
                          class="py-3 print:py-0.5 whitespace-nowrap px-3 text-sm text-gray-500"
                        >
                          {{ordEnt.amount}}

                        </td>
                        {{#if (eq @model.order.status "delivered")}}
                          <td
                            class="py-3 print:py-0.5 whitespace-nowrap px-3 text-sm
                              {{if
                                (eq ordEnt.amount ordEnt.amount_delivered)
                                'text-gray-500'
                                'text-red-500 font-bold'
                              }}"
                          >
                            {{ordEnt.amount_delivered}}

                          </td>
                        {{/if}}

                      </tr>

                      {{!-- {{#each @model.order.orderEntries as |ordEnt|}} --}}
                    {{/each}}
                  </tbody>
                </table>

              </div>

            </div>
            <div class="block mt-12">
              Vielen Dank!
            </div>
          </div>
        </div>
        {{! end:print }}
      </:default>
    </PageSetup>
  </template>
}
